import { Link } from 'react-router-dom'
import { Facebook, Instagram, Linkedin } from 'lucide-react'
import { tenantConfig } from '../../config/tenant'
import Nav from '../../components/Nav'
import Seo from '../../components/Seo'
import Footer from '../../components/Footer'
import LenderWall from '../../components/LenderWall'
import StructuredData from '../../components/StructuredData'
import { BROKERS } from '../../data/team'

/**
 * AboutPage — Greystone /about.
 *
 * Content reflects Chelston's 2026-09 sign-off:
 *  - Intro + Our Story rewritten (founder narrative, "more than mortgage brokers").
 *  - Purpose / Vision / Brand Promise + a "Our Values in Three Words" section added.
 *  - MFAA membership accreditation strip.
 *  - The fabricated testimonials block + static "105 reviews" badges REMOVED
 *    (real Google reviews live on the home page only).
 * Team = the 5 real broker cards (data in src/data/team.ts).
 */

export default function AboutPage() {
  return (
    <>
      <Seo
        title="About Us"
        path="/about"
        description="Meet Greystone Finance, a team of trusted Australian mortgage and finance brokers helping first home buyers, investors and business owners structure their finances and build long-term wealth."
      />
      <StructuredData />
      <Nav />
      <main id="main" className="pt-36 pb-20">
        {/* HERO */}
        <section className="max-w-5xl mx-auto px-6 pb-14 text-center">
          <span className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted">About</span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-ink font-semibold leading-tight mt-3 mb-6">
            Built on relationships,<br />powered by results.
          </h1>
          <p className="text-lg md:text-xl text-gs-text-soft max-w-2xl mx-auto leading-relaxed">
            A team of trusted mortgage brokers, simplifying the lending process for first home
            buyers, investors, and business owners.
          </p>
        </section>

        {/* STORY */}
        <section className="max-w-7xl mx-auto px-6 pb-16 lg:pb-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted">Our story</span>
              <h2 className="font-heading text-3xl lg:text-4xl text-ink font-semibold mt-2 mb-6">
                Why we started Greystone.
              </h2>
              <div className="space-y-4 text-gs-text-soft leading-relaxed">
                <p>
                  Founded in 2023 on a simple belief, securing finance shouldn&apos;t be confusing,
                  stressful, or treated as a one-size-fits-all process. Three experienced brokers came
                  together with a shared vision, to make lending personal again. With more than 40 years
                  of combined experience across banking and finance, we&apos;ve seen firsthand how complex
                  the lending process can become. Today, we use that experience to work for you.
                </p>
                <p className="font-heading text-xl text-ink font-semibold pt-2">
                  We&apos;re more than mortgage brokers.
                </p>
                <p>
                  We don&apos;t just help you secure a loan. We help you structure your finances
                  strategically, build long-term wealth, and achieve financial freedom sooner. By
                  combining deep banking knowledge, lending expertise, and property strategy, we empower
                  our clients to make confident, informed financial decisions.
                </p>
                <p>
                  Today, we work with over 60 lenders across Australia, providing access to a wide range
                  of solutions tailored to each client&apos;s goals. Despite our growth, our approach
                  remains personal. Every client is partnered with a dedicated broker who takes the time
                  to understand their circumstances, explain their options clearly, and provide ongoing
                  support well beyond settlement.
                </p>
                <p>
                  Whether you&apos;re purchasing your first home, upgrading to your next property, building
                  an investment portfolio, or financing a development project or growing your business,
                  we&apos;re here to provide trusted advice, strategic guidance, and long-term support, not
                  a sales pitch.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-6 mt-10 pt-10 border-t border-[#E8E8E2]">
                <div>
                  <div className="font-heading text-3xl font-bold text-ink">2023</div>
                  <div className="text-xs text-gs-text-muted mt-1 uppercase tracking-wider">Founded</div>
                </div>
                <div>
                  <div className="font-heading text-3xl font-bold text-ink">5</div>
                  <div className="text-xs text-gs-text-muted mt-1 uppercase tracking-wider">Brokers</div>
                </div>
                <div>
                  <div className="font-heading text-3xl font-bold text-ink">$300M+</div>
                  <div className="text-xs text-gs-text-muted mt-1 uppercase tracking-wider">Settled</div>
                </div>
              </div>
            </div>

            <div className="lg:sticky lg:top-28">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden relative">
                <img
                  src="/assets/services/shared-house.jpeg"
                  alt="Greystone Finance clients at home after settling their loan"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 p-6 pt-20 bg-gradient-to-t from-black/75 via-black/35 to-transparent">
                  <p className="font-heading text-2xl font-semibold text-white leading-tight">
                    &ldquo;Lending advice you can actually trust.&rdquo;
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PURPOSE / VISION / BRAND PROMISE */}
        <section className="max-w-7xl mx-auto px-6 pb-16 lg:pb-24">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                label: 'Purpose',
                body: 'To make lending personal again by delivering tailored finance solutions and strategic advice that help our clients build wealth and achieve financial freedom.',
              },
              {
                label: 'Vision',
                body: "To be Australia's most trusted mortgage and finance advisory, empowering clients to make smarter financial decisions and create long-term wealth with confidence.",
              },
              {
                label: 'Brand Promise',
                body: "More than a mortgage broker. We're your long-term finance and wealth partner.",
              },
            ].map((c) => (
              <div key={c.label} className="bg-white border border-[#E8E8E2] rounded-2xl p-7 flex flex-col">
                <div
                  className="text-xs font-semibold uppercase tracking-wider mb-3"
                  style={{ color: tenantConfig.primaryColor }}
                >
                  <span
                    className="inline-block w-8 h-1 rounded-full mr-2 align-middle"
                    style={{ background: tenantConfig.accentColor }}
                    aria-hidden="true"
                  />
                  {c.label}
                </div>
                <p className="text-gs-text-soft leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* TEAM */}
        <section id="team" className="max-w-7xl mx-auto px-6 py-16 lg:py-24" style={{ scrollMarginTop: '90px' }}>
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted">The team</span>
            <h2 className="font-heading text-3xl lg:text-5xl text-ink font-semibold mt-2 mb-4">
              Five brokers and the team behind them.
            </h2>
            <p className="text-lg text-gs-text-soft leading-relaxed">
              Every member of our team brings something different, but they all share a commitment to
              getting you the right outcome.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BROKERS.map((b) => (
              <article key={b.name} className="bg-white border border-[#E8E8E2] rounded-2xl p-7 card-hover flex flex-col">
                <div className="flex items-start justify-between gap-3 mb-5">
                  {b.image ? (
                    <img src={b.image} alt={b.name} className="w-36 h-36 rounded-2xl object-cover" loading="lazy" />
                  ) : (
                    <div className="w-36 h-36 rounded-2xl flex items-center justify-center bg-[#F4F4F0] border border-[#E8E8E2]" aria-hidden="true">
                      <span className="font-heading font-bold text-2xl text-ink">{b.initials}</span>
                    </div>
                  )}
                  <div className="flex gap-1.5">
                    {b.socials.facebook && (
                      <a href={b.socials.facebook} target="_blank" rel="noopener noreferrer" aria-label={`${b.name} on Facebook`} className="w-8 h-8 rounded-full grid place-items-center hover:opacity-80" style={{ background: tenantConfig.accentColor }}>
                        <Facebook size={15} className="text-ink" />
                      </a>
                    )}
                    {b.socials.instagram && (
                      <a href={b.socials.instagram} target="_blank" rel="noopener noreferrer" aria-label={`${b.name} on Instagram`} className="w-8 h-8 rounded-full grid place-items-center hover:opacity-80" style={{ background: tenantConfig.accentColor }}>
                        <Instagram size={15} className="text-ink" />
                      </a>
                    )}
                    {b.socials.linkedin && (
                      <a href={b.socials.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${b.name} on LinkedIn`} className="w-8 h-8 rounded-full grid place-items-center hover:opacity-80" style={{ background: tenantConfig.accentColor }}>
                        <Linkedin size={15} className="text-ink" />
                      </a>
                    )}
                  </div>
                </div>
                <h3 className="font-heading text-lg font-semibold text-ink">{b.name}</h3>
                <p className="text-gs-text-muted text-sm">{b.title}</p>
                <a href={`mailto:${b.email}`} className="text-sm text-gs-text-soft hover:text-ink break-all mt-1">{b.email}</a>
                <div className="text-gs-text-soft text-sm mt-4 space-y-3 leading-relaxed">
                  {b.bio.split('\n\n').map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
                {b.bookable !== false && (
                  <div className="mt-auto pt-6">
                    {b.calendly ? (
                      <a href={b.calendly} target="_blank" rel="noopener noreferrer" className="text-ink text-sm font-medium inline-flex items-center gap-1.5 hover:gap-2 transition-all">
                        Schedule a talk <span aria-hidden="true">↗</span>
                      </a>
                    ) : (
                      <Link to="/contact" className="text-ink text-sm font-medium inline-flex items-center gap-1.5 hover:gap-2 transition-all">
                        Book a call <span aria-hidden="true">→</span>
                      </Link>
                    )}
                  </div>
                )}
              </article>
            ))}
          </div>
        </section>

        {/* VALUES — dark band */}
        <section className="max-w-7xl mx-auto px-6 pb-16 lg:pb-24">
          <div
            className="relative overflow-hidden rounded-3xl px-8 py-14 md:px-16 md:py-16 text-center"
            style={{ background: tenantConfig.primaryColor }}
          >
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'radial-gradient(ellipse 520px 320px at 100% 0%, rgba(226,255,96,0.20), transparent 60%)',
              }}
              aria-hidden="true"
            />
            <div className="relative max-w-3xl mx-auto">
              <span className="text-xs font-semibold uppercase tracking-wider text-white/60">Our Values in Three Words</span>
              <h2 className="font-heading text-4xl md:text-6xl font-bold mt-3 mb-6" style={{ color: tenantConfig.accentColor }}>
                Trust. Strategy. Partnership.
              </h2>
              <p className="text-white/75 leading-relaxed md:text-lg">
                These three principles guide every conversation, every recommendation, and every client
                relationship at Greystone Finance. We are committed to being more than a mortgage broker.
                We are your long-term finance and wealth partner.
              </p>
            </div>
          </div>
        </section>

        {/* ACCREDITATION — MFAA */}
        <section className="max-w-7xl mx-auto px-6 pb-16 lg:pb-24">
          <div className="border-t border-[#E8E8E2] pt-12 flex flex-col items-center text-center gap-5">
            <span className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted">Accreditation</span>
            <div className="flex flex-col sm:flex-row items-center gap-5">
              <span
                className="inline-flex items-center gap-2 rounded-xl border-2 px-5 py-3 font-heading font-bold tracking-tight"
                style={{ borderColor: tenantConfig.primaryColor, color: tenantConfig.primaryColor }}
                aria-label="MFAA Member"
              >
                <span className="text-2xl">MFAA</span>
                <span className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted">Member</span>
              </span>
              <p className="text-gs-text-soft text-sm md:text-base max-w-md text-left">
                Proud member of the <span className="font-semibold text-ink">MFAA</span> (Mortgage &amp;
                Finance Association of Australia), Australia&apos;s leading professional body for mortgage
                and finance brokers.
              </p>
            </div>
          </div>
        </section>

        {/* LENDER MARQUEE */}
        <LenderWall />
      </main>
      <Footer />
    </>
  )
}
